import { api } from './api';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface CustomLLDClarification {
 question: string;
 answer: string;
}

export interface CustomLLDExpectedEntity {
 name: string;
 description: string;
 attributes: object[];
 methods: object[];
}

export interface CustomLLDExpectedRelationship {
 source: string;
 target: string;
 type: string;
 cardinality: string;
}

export interface CustomLLDTestCase {
 name: string;
 category: 'functional' | 'edge-case';
 input: string;
 expectedOutput: string;
 assertion: string;
}

export type CustomLLDDifficulty = 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
export type CustomLLDCategory =
 | 'Object Modeling'
 | 'System Management'
 | 'Payment and Splitting'
 | 'Booking and Reservation'
 | 'Real-Time Services';

/**
 * Payload accepted by create/update. Mirrors the backend CustomLLDProblemDto.
 * Only `title` and `description` are required; everything else is optional.
 */
export interface CustomLLDProblemInput {
 title: string;                       // required, <= 200
 description: string;                 // required, <= 5000
 difficulty?: CustomLLDDifficulty;
 category?: CustomLLDCategory;
 clarifications?: CustomLLDClarification[];
 expectedEntities?: CustomLLDExpectedEntity[];        // <= 50
 expectedRelationships?: CustomLLDExpectedRelationship[]; // <= 100
 testCases?: CustomLLDTestCase[];                     // <= 50
}

/**
 * A persisted custom problem returned by the API.
 * The `id` is namespaced with the `CLLD@` prefix to avoid colliding with curated ids.
 */
export interface CustomLLDProblem extends CustomLLDProblemInput {
 id: string;            // "CLLD@<uuid>"
 ownerId: string;
 isCustom: true;        // discriminator flag in API responses for the UI
 createdAt: string;
 updatedAt: string;
}

const BASE = '/lld-practice/custom-problems';

class LLDCustomProblemService {
 /**
  * List the authenticated user's own custom problems, newest first.
  * Requires JWT authentication.
  */
 async listMine(): Promise<CustomLLDProblem[]> {
  return await api.get(BASE);
 }

 /**
  * Fetch a single owned custom problem by id (used to pre-fill the edit form).
  * Requires JWT authentication.
  */
 async getById(id: string): Promise<CustomLLDProblem> {
  return await api.get(`${BASE}/${encodeURIComponent(id)}`);
 }

 /**
  * Create a new custom problem owned by the authenticated user.
  * Requires JWT authentication.
  */
 async create(input: CustomLLDProblemInput): Promise<CustomLLDProblem> {
  return await api.post(BASE, input);
 }

 /**
  * Update an owned custom problem.
  * Requires JWT authentication.
  */
 async update(id: string, input: CustomLLDProblemInput): Promise<CustomLLDProblem> {
  return await api.put(`${BASE}/${encodeURIComponent(id)}`, input);
 }

 /**
  * Delete an owned custom problem (cascades to its progress on the backend).
  * Requires JWT authentication.
  */
 async remove(id: string): Promise<void> {
  return await api.delete(`${BASE}/${encodeURIComponent(id)}`);
 }
}

export const lldCustomProblemService = new LLDCustomProblemService();
export default lldCustomProblemService;
